<template>
  <div class="d-flex flex-wrap ga-2 p-4 form-card col-sm-12 col-xl-6">
    <h4 class="w-100 mb-2 font-weight-medium">Add Summary</h4>

    <!-- Select Month -->
    <div class="col-sm-12">
      <v-select
        v-model="form.investment_summary_id"
        :items="summaryOptions"
        :item-title="item => $helper.formatDate(item.label, true)"
        item-value="code"
        label="Select Month"
        return-object
        variant="solo"
        density="compact"
        hide-details
        class="full-width"
      />
    </div>

    <!-- Select Type -->
    <div class="col-sm-12">
      <v-select
        v-model="form.investment_type_id"
        :items="typeOptions"
        item-title="label"
        item-value="code"
        label="Select Type"
        return-object
        variant="solo"
        density="compact"
        taggable
        hide-details
        class="full-width"
        v-model:search="customTypeInput"
        @blur="handleCustomType"
        @change="onTypeChange"
      />
    </div>

    <!-- New Text Box for Custom Type -->
    <div class="col-sm-12" v-if="showCustomTypeInput">
      <v-text-field
        v-model="newType"
        label="Enter Custom Type"
        variant="solo"
        density="compact"
        hide-details
        class="full-width"
        @blur="addCustomType"
      />
    </div>

    <!-- Investment Amount -->
    <div class="col-sm-12">
      <v-text-field
        v-model="form.invest"
        type="number"
        label="Investment Amount"
        variant="solo"
        density="compact"
        hide-details
        class="full-width"
      />
    </div>

    <!-- Date -->
    <div class="col-sm-12">
      <v-text-field
        v-model="form.created_at"
        type="date"
        label="Date"
        variant="solo"
        density="compact"
        hide-details
        class="full-width"
      />
    </div>

    <!-- Submit Button -->
    <div class="form-floating col-sm-12">
      <v-btn
        :disabled="v$.form.$invalid"
        color="primary"
        variant="tonal"
        rounded="lg"
        @click="AddInvestment"
      >
        Submit
      </v-btn>
    </div>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { useAlertStore } from '@/stores';
import gql from 'graphql-tag'

const TYPES_OPTION_QUERY = gql`
  query Types {
    types {
      investment_type_id
      name
    }
  }
`;

const SUMMARY_OPTION_QUERY = gql`
  query Query {
    summarys {
      investment_summary_id,
      created_at
    }
  }
`;

const ADD_INVESTMENT_QUERY = gql`
  mutation AddInvestment($invest: Float!, $investmentTypeId: Option!, $investmentSummaryId: Option!, $createdAt: String) {
    addInvestment(invest: $invest, investment_type_id: $investmentTypeId, investment_summary_id: $investmentSummaryId, created_at: $createdAt) {
      investment_id
      investment_summary_id
      investment_type_id
      invest
      created_at
    }
  }
`;

export default {
  name: 'investment-plan-add-summary',
  setup() {
    return { 
      v$: useVuelidate(),
      alertStore$: useAlertStore(),
    };
  },
  data() {
    return {
      form: {
        investment_summary_id: { label: '', code: '' },
        investment_type_id: { label: '', code: '' },
        invest: "",
        created_at: "",
      },
      summarys: [],
      types: [],
      typeOptions: [],
      customTypeInput: "",
      newType: "", // This stores the new custom type input
      showCustomTypeInput: false,
    };
  },
  validations() {
    return {
      form: {
        investment_summary_id: { required },
        investment_type_id: { required },
        invest: { required },
        created_at: { required },
      },
    };
  },
  watch: {
    summarys(val) {
      this.summaryOptions = val.map(item => ({
        label: item.created_at, // Optionally format this date
        code: item.investment_summary_id
      }));
    },

    types(val) {
      const baseOptions = val.map(item => ({
        label: item.name,
        code: item.investment_type_id
      }));

      // Append custom option
      this.typeOptions = [
        ...baseOptions,
        { label: 'Add Custom Option', code: 'custom' }
      ];
    },

    'form.investment_type_id.code'(newValue) {
      this.showCustomTypeInput = newValue === 'custom';
    },
  },
  methods: {
    addCustomType() {
      const input = this.newType?.trim();

      if (
        input &&
        !this.typeOptions.some(opt => opt.label.toLowerCase() === input.toLowerCase())
      ) {
        const newOption = {
          label: input,
          code: `new_${Date.now()}`
        };
        this.typeOptions.push(newOption);
        this.form.investment_type_id = newOption; // ✅ Assign full object, not just code
      }

      this.newType = ""; // Reset input field
    },
    // onTypeChange(selectedType) {
    //   // Handle custom logic if needed when a type is selected
    //   console.log(selectedType); // You can use this to log the selection or do further processing.
    // },
    AddInvestment() {
      try {

        const selectedType = this.form.investment_type_id;
        const isCustomType = selectedType.code.startsWith('new_');

        const investmentTypeToSend = isCustomType
          ? { code: "", label: selectedType.label }
          : { code: selectedType.code, label: selectedType.label };

        this.$apollo.mutate({
          mutation: ADD_INVESTMENT_QUERY,
          variables: {
            investmentSummaryId: this.form.investment_summary_id,
            investmentTypeId: investmentTypeToSend,
            invest: parseFloat(this.form.invest),
            createdAt: this.form.created_at
          },
        }).then(() => {
          this.alertStore$.success('New investment added successfully.');
          this.form = {
            investment_summary_id: "",
            investment_type_id: "",
            invest: "",
            created_at: ""
          };
        }).catch((error) => {
          this.alertStore$.error(JSON.stringify(error.message));
        });
      } catch (error) {
        this.alertStore$.error(JSON.stringify(error.message));
      }
    },
  },
  apollo: {
    summarys: {
      query: SUMMARY_OPTION_QUERY,
    },
    types: {
      query: TYPES_OPTION_QUERY,
    },
  },
}
</script>

<style scoped>
.form-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
}
</style>